import { useRef, Suspense } from "react";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  SvgIcon,
  LinearProgress,
  Drawer,
  SwipeableDrawer,
  Fab,
  Grid,
  Hidden,
  Portal,
  Snackbar,
  Toolbar,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { MapEventsContextProvider } from "components/common/MapEventsContext";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import Header from "components/common/Header";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { BASEMAPS } from "@carto/react-basemaps";
import Map from "components/common/map/Map";
import ZoomControl from "components/common/ZoomControl";
import { getLayers } from "components/layers";
import { setBottomSheetOpen, setError } from "store/appSlice";
import cartoLogoMap from "assets/img/carto-logo-map.svg";
import AnalysisFloatingMenu from "components/views/AnalysisFloatingMenu";
import AnalysisFormOverlay from "components/views/AnalysisFormOverlay";
import WidgetsPanel from "components/views/WidgetsPanel";
import GeocoderWidget from "components/views/components/GeocoderWidget";
import { LegendWidget } from "@carto/react-widgets";
import BasemapWidget from "components/common/BasemapWidget";
import ConfigPanel from "components/views/ConfigPanel";

const drawerWidth = 360;

const useStyles = makeStyles((theme) => ({
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    marginTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    overflow: "hidden",
  },
  widgetDrawerToggle: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    zIndex: 1,
    textAlign: "center",
  },
  bottomSheet: {
    maxHeight: `calc(100% - ${theme.spacing(6)}px)`,

    "&$closed": {
      transform: `translateY(calc(100% - ${theme.spacing(12)}px)) !important`,
      visibility: "visible !important",

      "& $bottomSheetContent": {
        overflow: "hidden",
      },
    },
  },
  closed: {},
  bottomSheetContent: {
    minHeight: theme.spacing(18),
    "& > *": {
      paddingBottom: theme.spacing(6),
    },
  },
  bottomSheetButton: {
    position: "absolute",
    bottom: theme.spacing(5),
    right: theme.spacing(2),
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    transform: `translateY(${theme.spacing(3)}px)`,
    transition: `transform ${theme.transitions.easing.sharp} ${theme.transitions.duration.shortest}ms`,

    "&:hover": {
      backgroundColor: theme.palette.common.white,
    },

    "& .MuiFab-label": {
      width: theme.spacing(9),
      justifyContent: "flex-start",
    },

    "&$buttonShow": {
      transform: "translateY(0)",

      "& $bottomSheetIcon": {
        transform: "rotate(0)",
      },
    },
  },
  bottomSheetIcon: {
    color: theme.palette.text.hint,
    height: theme.spacing(4),
    transform: "rotate(180deg)",
  },
  buttonShow: {},
  mapWrapper: {
    position: "relative",
    flex: 1,
    overflow: "hidden",

    // Fix Mapbox attribution button not clickable
    "& #deckgl-wrapper": {
      "& #deckgl-overlay": {
        zIndex: 1,
      },
      "& #view-default-view > div": {
        zIndex: "auto !important",
      },
    },
  },
  geocoder: {
    position: "absolute",
    top: theme.spacing(3),
    left: "50%",
    transform: "translateX(-50%)",

    [theme.breakpoints.down("xs")]: {
      width: `calc(100% - ${theme.spacing(6)}px)`,
    },
  },
  zoomControl: {
    position: "absolute",
    bottom: theme.spacing(4),
    left: theme.spacing(4),
    zIndex: 1,

    [theme.breakpoints.down("sm")]: {
      bottom: theme.spacing(4),
      left: theme.spacing(2),
    },
  },
  cartoLogoMap: {
    position: "absolute",
    bottom: theme.spacing(4),
    left: "50%",
    transform: "translateX(-50%)",

    [theme.breakpoints.down("sm")]: {
      bottom: theme.spacing(4.75),
    },

    [theme.breakpoints.down("xs")]: {
      bottom: theme.spacing(13.5),
    },
  },
  gmaps: {
    "& $zoomControl": {
      left: theme.spacing(4),
      bottom: theme.spacing(5),
    },
  },
  basemapWidget: {
    position: "absolute",
    top: theme.spacing(3),
    left: theme.spacing(3),
    zIndex: 1,
  },

  widgetDrawerToggle: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    zIndex: 1,
    textAlign: "center",
  },
  legend: {
    position: "absolute",
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1,

    [theme.breakpoints.down("sm")]: {
      bottom: theme.spacing(10),
      right: theme.spacing(2),
    },

    [theme.breakpoints.down("xs")]: {
      bottom: theme.spacing(18.5),
      right: theme.spacing(2),
    },
  },
  visitorsComponent: {
    position: "absolute",
    top: theme.spacing(3),
    left: theme.spacing(3),
    zIndex: 1,
  },
}));

const ArrowIcon = ({ className }) => (
  <SvgIcon width="24" height="8" viewBox="0 0 24 8" className={className}>
    <path
      d="M176 18.214L188 12 200 18.214 199.138 20 188 14.233 176.862 20z"
      transform="translate(-176 -606) translate(0 594)"
      fill="inherit"
    />
  </SvgIcon>
);

function OutletWithOverlay() {
  return (
    <Box
      maxWidth="100%"
      width="inherit"
      position="relative"
      maxHeight="calc(100vh - 48px)"
      overflow="auto"
    >
      <Suspense fallback={<LinearProgress />}>
        <AnalysisFormOverlay />
      </Suspense>
      <Outlet />
    </Box>
  );
}
function DesktopDrawer() {
  const classes = useStyles();

  return (
    <Hidden xsDown>
      <Drawer
        classes={{
          paper: classes.drawerPaper,
        }}
        variant="permanent"
        anchor="right"
        open
      >
        <ConfigPanel />
        <OutletWithOverlay />
      </Drawer>
    </Hidden>
  );
}

function MobileDrawer() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const bottomSheetOpen = useSelector((state) => state.app.bottomSheetOpen);

  const handleWidgetsDrawerToggle = () => {
    dispatch(setBottomSheetOpen(!bottomSheetOpen));
  };

  return (
    <Hidden smUp>
      <SwipeableDrawer
        variant="persistent"
        anchor="bottom"
        open={bottomSheetOpen}
        onOpen={handleWidgetsDrawerToggle}
        onClose={handleWidgetsDrawerToggle}
        PaperProps={{ className: classes.bottomSheet }}
      >
        <Box
          display="flex"
          justifyContent="center"
          onClick={handleWidgetsDrawerToggle}
        >
          <ArrowIcon className={classes.bottomSheetIcon} />
        </Box>
        <div className={classes.bottomSheetContent}>
          <OutletWithOverlay />
        </div>
      </SwipeableDrawer>
    </Hidden>
  );
}

function Main() {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const mobileContainer = useRef(null);
  const desktopContainer = useRef(null);
  const dispatch = useDispatch();
  const error = useSelector((state) => state.app.error);
  const bottomSheetOpen = useSelector((state) => state.app.bottomSheetOpen);
  const isGmaps = useSelector(
    (state) => BASEMAPS[state.carto.basemap].type === "gmaps"
  );
  const isDropAPin = useSelector((state) => state.app.isDropAPin);

  // [hygen] Add useEffect

  const handleClose = () => {
    dispatch(setError(null));
  };

  const handleWidgetsDrawerToggle = () => {
    dispatch(setBottomSheetOpen(!bottomSheetOpen));
  };

  return (
    <>
      <MapEventsContextProvider>
        <Grid container direction="row" alignItems="stretch" item xs>
          <Grid
            item
            className={`${classes.mapWrapper} ${isGmaps ? classes.gmaps : ""}`}
          >
            <Map layers={getLayers()} />
            <LegendWidget id="legend" className={classes.legend} />
            <BasemapWidget id="switcher" className={classes.basemapWidget} />
            <WidgetsPanel className={classes.visitorsComponent} />
            {isDropAPin && (
              <GeocoderWidget
                id="geocode"
                className={classes.geocoder}
                onError={() =>
                  dispatch(setError(`Geocoding error: ${error.message}`))
                }
              />
            )}
            {isDropAPin && <AnalysisFloatingMenu />}
            <Hidden xsDown>
              <ZoomControl className={classes.zoomControl} />
            </Hidden>
            {!isGmaps && (
              <img
                src={cartoLogoMap}
                alt="CARTO"
                className={classes.cartoLogoMap}
              />
            )}
          </Grid>
          <nav className={classes.drawer}>
            <DesktopDrawer />
            <MobileDrawer />
          </nav>
          <Snackbar
            open={!!error}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <Alert severity="error">{error}</Alert>
          </Snackbar>
        </Grid>
      </MapEventsContextProvider>
    </>
  );
}

export default Main;
